import { CheckCircle2, CircleDashed, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { useScenarioGate } from '@/hooks/useScenarioRuns';
import { channelLabel } from '@/lib/api/scenario-runs.api';
import { cn } from '@/lib/utils';

interface ScenarioGateBadgeProps {
  configurationId: string;
  commitId?: string;
  className?: string;
}

/**
 * Shows whether the required scenarios passed on the given commit (or the branch
 * head when no commit is given). Hidden while the gate is disabled.
 */
export const ScenarioGateBadge = ({
  configurationId,
  commitId,
  className,
}: ScenarioGateBadgeProps) => {
  const { data: gate } = useScenarioGate(configurationId, commitId);

  if (!gate || !gate.enabled || gate.status === 'disabled') return null;

  const failing = gate.required.filter((r) => r.result !== 'pass');
  const title = failing.length
    ? failing
        .map((r) => `${r.scenario_name} on ${channelLabel(r)}: ${r.result.replace('_', ' ')}`)
        .join('\n')
    : 'All required scenarios passed';

  const text =
    gate.status === 'pass'
      ? 'Required scenarios: pass'
      : gate.status === 'fail'
        ? `Required scenarios: fail (${failing.length})`
        : 'Required scenarios: not run';

  const tone =
    gate.status === 'pass'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'
      : gate.status === 'fail'
        ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300'
        : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300';

  const Icon =
    gate.status === 'pass' ? CheckCircle2 : gate.status === 'fail' ? XCircle : CircleDashed;

  return (
    <Badge
      variant="outline"
      className={cn('gap-1 whitespace-nowrap', tone, className)}
      title={title}
    >
      <Icon className="h-3 w-3" />
      {text}
    </Badge>
  );
};
